import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ProductCustomisationDetailsResponse, ProductCustomisationsService, ProductAttributeDefinitionsService, ProductAttributeDefinitionDetailsMinimalResponse, FilterRequest } from '@src/app/shared/backend/clients';
import { ConfirmationService } from 'primeng/api';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { SessionService } from '@app/shared/services/session.service';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';


@Component({
    selector: 'app-view-product-customizations',
    templateUrl: './view-product-customizations.component.html',
    styleUrl: './view-product-customizations.component.scss',
    host: {
        class: 'app-view-product-customizations'
    },
    providers: [ConfirmationService],
    standalone: false
})
export class ViewProductCustomizationsComponent extends BaseViewComponent implements OnInit, OnDestroy {

  private readonly destroyedComponent$ = new Subject<void>();

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  productAttributesFeatureEnabled: boolean = false;
  showProductAttributeDetails: boolean = false;
  canViewPrice: boolean = false;
  delete: boolean = false;
  allowModifyProductCustomisation: boolean = false;
  allowViewProduct: boolean = false;
  productCustomizationId: number = 0;
  translatedProductAttributes: { code: string, name: string; value: string, changesOverTime: boolean }[] = [];
  productCustomization: ProductCustomisationDetailsResponse = {
    id: 0,
    productId: 0,
    productName: ''
  };
  productAttributeDefinitions: ProductAttributeDefinitionDetailsMinimalResponse[] = [];

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private session: SessionService,
    private translate: TranslateService,
    private productAttributeDefinitionsService: ProductAttributeDefinitionsService,
    private productCustomisationsService: ProductCustomisationsService) {
    super();
  }


  async ngOnInit() {
    const company = await this.session.getCompanyData();
    this.canViewPrice = company.hasPrice ?? false;
    this.showProductAttributeDetails = company?.showProductAttributeDetails ?? false;
    const companySettings = await this.session.getCompanySettings();
    this.productAttributesFeatureEnabled = companySettings.features?.some(f => f.name === 'ProductAttributes' && f.isEnabled) ?? false;
    this.init();
  }

  private async init(): Promise<void> {

   this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(async (params) => {
        this.productCustomizationId = Number(params['productCustomizationId']);
        if (this.productCustomizationId > 0) {
          await this.getProductAttributesDefinitions();
          await this.get();
        }
      });

    this.delete = await this.permissionService.hasActionPermissions(Actions.delete);
    this.allowModifyProductCustomisation = await this.permissionService.hasActionPermissions(Actions.modify_productcustomisations);
    this.allowViewProduct = await this.permissionService.hasActionPermissions(Actions.view_products);
  }

  override async get() {
    try {
      this.productCustomization = await firstValueFrom(this.productCustomisationsService.apiProductCustomisationsDetailsIdGet(this.productCustomizationId));
      this.fillTranslatedAttributes();
    } catch (error) {
      console.log('Error fetching productCustomization data:', error);
    }
  }

  goToEditProductCustomization() {
    this.router.navigate([`/product-customisations/${this.productCustomizationId}/edit`]);
  }

  async showDeleteDialog(): Promise<void> {
    const deleteCheckResponses = await firstValueFrom(this.productCustomisationsService.apiProductCustomisationsDeleteCheckBatchPost([this.productCustomizationId]));

    this.dialog?.show(deleteCheckResponses);
  }

  async deleteProductCustomization(doDelete: boolean): Promise<void> {
    if (doDelete) {
      await firstValueFrom(this.productCustomisationsService.apiProductCustomisationsDeleteIdDelete(this.productCustomizationId));
      this.router.navigate([`/product-customisations`]);
    }
  }

   async getProductAttributesDefinitions() {
      if (!this.productAttributesFeatureEnabled) {
        this.productAttributeDefinitions = [];
        this.translatedProductAttributes = [];
        return;
      }

      try {
        const filter: FilterRequest = {
          pageSize: 500,
          pageNumber: 1,
          filters: [
          {
            propertyName: 'isActive',
            operator: FilterOperator.EQUALS,
            values: ['true']
          }]
        };

        this.productAttributeDefinitions = (await firstValueFrom(this.productAttributeDefinitionsService.apiProductAttributeDefinitionsFilterDefinitionsOnlyPost(filter))).results ?? [];

      } catch (error) {
        console.log('Error fetching product attributes definition data:', error);
      }
    }

  fillTranslatedAttributes(){
    this.translatedProductAttributes = this.productAttributeDefinitions?.map(def => {
      const translatedName = def.productAttributeDefinitionTranslations?.find(t => t.isoCode === this.translate.currentLang)?.translatedName
        ?? (def.productAttributeDefinitionTranslations?.find(t => t.isoCode === 'en')?.translatedName ?? def.definitionCode);
      const productAttributeValues = this.productCustomization.productAttributeValues?.filter(attrValue => attrValue.productAttributeDefinitionId === def.id);
      let translatedValue = "-";
      if (productAttributeValues) {
        const translatedValues = productAttributeValues.map(productAttributeValue =>
          productAttributeValue.productAttributeValueTranslations?.find(t => t.isoCode === this.translate.currentLang)?.translatedValue
            ?? (productAttributeValue.productAttributeValueTranslations?.find(t => t.isoCode === 'en')?.translatedValue ?? productAttributeValue.valueCode));
        translatedValue = translatedValues.join(", ");
      }
      return { code: def.definitionCode, name: translatedName, value: translatedValue, changesOverTime: def.changesOverTime };
    }) ?? [];
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
